/* Floating alert that appears in the bottom-right corner with auto-dismiss.
   Built entirely with Tailwind + shadcn design tokens — no third-party toast library. */

import { useState, useEffect, useCallback, useRef } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface FloatingAlertProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  actionLabel?: string;
  onAction?: () => void;
  duration?: number;
}

export function FloatingAlert({
  open,
  onOpenChange,
  title,
  description,
  actionLabel,
  onAction,
  duration = 8000,
}: FloatingAlertProps) {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => setVisible(true));
      timerRef.current = setTimeout(() => onOpenChangeRef.current(false), duration);
      return () => clearTimeout(timerRef.current);
    } else {
      setVisible(false);
    }
  }, [open, duration]);

  const dismiss = useCallback(() => {
    clearTimeout(timerRef.current);
    onOpenChange(false);
  }, [onOpenChange]);

  const handleAction = useCallback(() => {
    clearTimeout(timerRef.current);
    onAction?.();
    onOpenChange(false);
  }, [onAction, onOpenChange]);

  if (!open) return null;

  return (
    <div
      className={cn(
        "fixed bottom-4 right-4 z-[9999] max-w-sm w-full",
        "rounded-lg border border-border bg-card text-card-foreground shadow-lg",
        "p-4 transition-all duration-300 ease-out",
        visible
          ? "translate-y-0 opacity-100"
          : "translate-y-2 opacity-0",
      )}
    >
      <button
        onClick={dismiss}
        className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors"
      >
        <X className="size-4" />
      </button>

      <p className="text-sm font-semibold pr-6">{title}</p>
      <p className="text-sm text-muted-foreground mt-1">{description}</p>

      {actionLabel && onAction && (
        <Button
          variant="default"
          size="sm"
          className="mt-3"
          onClick={handleAction}
        >
          {actionLabel}
        </Button>
      )}
    </div>
  );
}
